/**
 * Per-scope, TTL-bounded cache.
 *
 * Each MCP server process can hold state for many workspaces at once (HTTP mode:
 * one process, many API keys — see auth.ts). Caches must therefore be partitioned
 * by request scope, never stored as a single module global, or one workspace's
 * data leaks to another (TEN-2415). The TTL bounds staleness so a mid-session
 * structure change becomes visible without a process restart (TEN-2398).
 *
 * Bounded by maxScopes with TTL-first eviction, mirroring auth.ts keyStateMap
 * (evictStale + MAX_KEYS). Without this, a long-lived multi-tenant process would
 * accumulate one entry per API key that ever connected — the in-process per-key
 * Map scale ceiling called out in TEN-2229.
 */
interface Entry<T> {
  value: T;
  expiresAt: number;
}

export class ScopedCache<T> {
  private store = new Map<string, Entry<T>>();
  constructor(
    private readonly ttlMs: number,
    private readonly maxScopes = 100, // matches auth.ts MAX_KEYS — can't have more live scopes than live keys
  ) {}

  get(scope: string): T | undefined {
    const e = this.store.get(scope);
    if (!e) return undefined;
    if (Date.now() > e.expiresAt) {
      this.store.delete(scope);
      return undefined;
    }
    return e.value;
  }

  set(scope: string, value: T): void {
    this.store.set(scope, { value, expiresAt: Date.now() + this.ttlMs });
    if (this.store.size > this.maxScopes) this.evict();
  }

  delete(scope: string): void {
    this.store.delete(scope);
  }

  clear(): void {
    this.store.clear();
  }

  size(): number {
    return this.store.size;
  }

  /** Drop expired entries first; if still over budget, drop the oldest-expiring. */
  private evict(): void {
    const now = Date.now();
    for (const [k, e] of this.store) {
      if (now > e.expiresAt) this.store.delete(k);
    }
    if (this.store.size > this.maxScopes) {
      const sorted = [...this.store.entries()].sort((a, b) => a[1].expiresAt - b[1].expiresAt);
      const overBy = sorted.length - this.maxScopes; // capture once — store mutates in the loop
      for (let i = 0; i < overBy; i++) {
        this.store.delete(sorted[i][0]);
      }
    }
  }
}

/** Collection-structure caches share this window. Matches the readCache TTL in client.ts. */
export const COLLECTION_CACHE_TTL_MS = 60_000;
